import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import OrganizationPropertiesEditor from '../components/OrganizationPropertiesEditor';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/authService';
import { FASTCUBE_ORG, organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';

function normalizeOrgs(orgs) {
  return [...(orgs || [])]
    .map((o) => ({ name: o.name, id: String(o.id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function OrganizationProperties() {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadOrganizations = async () => {
      setLoading(true);
      try {
        let orgs;
        if (isAdmin) {
          const res = await api.get('/api/v2/admin/organizations');
          orgs = normalizeOrgs(res.data);
        } else {
          orgs = normalizeOrgs(await organizationsService.getOrganizations());
        }
        if (cancelled) return;
        setOrganizations(orgs);

        const savedId = organizationsService.getSelectedOrganization('id');
        const savedName = organizationsService.getSelectedOrganization('name');
        const fromId = orgs.find((o) => o.id === String(savedId));
        const fromName = orgs.find((o) => o.name === savedName);
        const selected = fromId || fromName || orgs.find((o) => o.name !== FASTCUBE_ORG) || orgs[0];
        if (selected) {
          setSelectedOrganization(selected.id);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
        if (!cancelled) setOrganizations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    const sync = () => {
      const savedId = organizationsService.getSelectedOrganization('id');
      if (savedId) setSelectedOrganization(String(savedId));
    };
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, sync);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, sync);
  }, []);

  const selectedOrg = organizations.find((o) => o.id === String(selectedOrganization));
  const isFastCube = selectedOrg?.name === FASTCUBE_ORG;
  const apiBase = isAdmin ? '/api/v2/admin/organizations' : '/api/v2/organizations';

  const handleOrganizationChange = (value) => {
    const parsed = organizationsService.parseOrganizationOptionValue(value);
    const nextId = parsed ? parsed.id : value;
    setSelectedOrganization(nextId);
    organizationsService.setSelectedOrganization(nextId, 'id', organizations);
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-visible">
        <CardHeader>
          <CardTitle>Organization properties</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 overflow-visible">
          <div className="max-w-md">
            <FunctionalSelect
              label="Organization"
              value={organizationsService.getSelectedOptionValue(
                organizations,
                selectedOrganization,
                'id'
              )}
              onValueChange={handleOrganizationChange}
              placeholder={loading ? 'Loading organizations…' : 'Select organization'}
              disabled={loading || organizations.length === 0}
            >
              {organizations.map((org) => (
                <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                  {org.name}
                </SelectItem>
              ))}
            </FunctionalSelect>
          </div>
          {!loading && organizations.length === 0 ? (
            <p className="text-sm text-amber-700">
              No organizations available. Ask an administrator to add you to an organization.
            </p>
          ) : (
            <OrganizationPropertiesEditor
              organizationId={selectedOrganization}
              apiBase={apiBase}
              disabled={loading || !selectedOrganization || isFastCube}
              disabledReason={
                isFastCube
                  ? 'FastCube has no universe table, so custom properties are not available. Select another organization to manage its properties.'
                  : 'Select an organization to manage custom properties.'
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default OrganizationProperties;
